import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import "./PDFViewerModal.css";

const PDFViewerModal = ({ isOpen, onClose, pdfUrl, title, language }) => {
  // Lock body scroll while open. Outside-click is handled on the overlay
  // itself (see handleOverlayClick) — listening on document with contains()
  // misidentifies taps inside the PDF iframe as "outside" and closes the modal
  // when the user tries to navigate pages.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('pdf-viewer-modal-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('pdf-viewer-modal-open');
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('pdf-viewer-modal-open');
    };
  }, [isOpen]);

  // Detect iOS — iframe PDFs don't accept touch scroll on iOS WebKit.
  // We use Google Docs viewer as a fallback so the user can swipe to scroll.
  const isIOS = typeof navigator !== 'undefined' &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !pdfUrl) {
    return null;
  }

  const handleShareClick = () => {
    if (navigator.share) {
      navigator.share({
        title: title,
        url: pdfUrl,
      }).catch((error) => {
        fallbackShare();
      });
    } else {
      fallbackShare();
    }
  };

  const fallbackShare = () => {
    navigator.clipboard.writeText(pdfUrl).then(() => {
      alert(language === "ar" ? "تم نسخ رابط الملف إلى الحافظة" : "PDF link copied to clipboard");
    }).catch(() => {
      window.open(`https://wa.me/?text=${encodeURIComponent(pdfUrl)}`, '_blank');
    });
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const stopTouchPropagation = (e) => {
    e.stopPropagation();
  };

  // Render outside any parent modal's pointer-events:none overlay
  // so iOS reliably routes touches to the PDF instead of the map.
  return createPortal(
    <div
      className="pdf-viewer-modal-overlay"
      onClick={handleOverlayClick}
      onTouchStart={stopTouchPropagation}
      onTouchMove={stopTouchPropagation}
      onTouchEnd={stopTouchPropagation}
    >
      <div
        className={`pdf-viewer-modal ${language === "ar" ? "rtl" : "ltr"}`}
        dir={language === "ar" ? "rtl" : "ltr"}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={stopTouchPropagation}
        onTouchMove={stopTouchPropagation}
        onTouchEnd={stopTouchPropagation}
      >
        {/* Header */}
        <div className="pdf-viewer-header">
          <h3 className="pdf-viewer-title">
            {title || (language === "ar" ? "كتيب المشروع" : "Project Brochure")}
          </h3>
          <div className="pdf-viewer-controls">
            <button 
              className="pdf-viewer-share-btn"
              onClick={handleShareClick}
              title={language === "ar" ? "مشاركة الملف" : "Share PDF"}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                <polyline points="16,6 12,2 8,6"/>
                <line x1="12" y1="2" x2="12" y2="15"/>
              </svg>
            </button>
            <button 
              className="pdf-viewer-close-btn"
              onClick={onClose}
              title={language === "ar" ? "إغلاق" : "Close"}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="pdf-viewer-content">
          {isIOS ? (
            // iOS WebKit doesn't allow touch-scrolling inside a PDF iframe.
            // Use Google Docs viewer (HTML rendering, fully touch-scrollable)
            // and offer a direct "open" fallback if the viewer fails.
            <>
              <iframe
                src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(pdfUrl)}`}
                title={title || "Project Brochure"}
                className="pdf-viewer-iframe"
                frameBorder="0"
                allowFullScreen
              />
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="pdf-viewer-ios-open-btn"
              >
                {language === "ar" ? "فتح الملف في تطبيق جديد" : "Open PDF in new tab"}
              </a>
            </>
          ) : (
            <iframe
              src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
              title={title || "Project Brochure"}
              className="pdf-viewer-iframe"
              frameBorder="0"
              allowFullScreen
            />
          )}
        </div>

        {/* Footer with download link */}
        {/* <div className="pdf-viewer-footer">
          <a 
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pdf-viewer-download-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7,10 12,15 17,10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {language === "ar" ? "تحميل الملف" : "Download PDF"}
          </a>
        </div> */}
      </div>
    </div>,
    document.body
  );
};

export default PDFViewerModal;
